import type { ErrorEvent } from '@sentry/vue';

// Kept dependency-free: the embedded CX and signup entries import this for their Sentry filter.

// What each engine throws when a lazy chunk (or the CSS Vite preloads for it) can't be fetched —
// almost always a tab still running a build whose hashed assets the last deploy replaced.
const CHUNK_LOAD_ERROR_PATTERNS = [
    /Failed to fetch dynamically imported module/i, // Chromium
    /error loading dynamically imported module/i, // Firefox
    /Importing a module script failed/i, // Safari
    /Unable to preload CSS for/i, // Vite's preload helper
    /is not a valid JavaScript MIME type/i // a missing asset answered with the SPA's index.html
];

// A chunk failure marks the tab; the next page load (whatever reloads it — recovery, idle update,
// F5) converts the mark into a "recovered at" time. A chunk failure shortly after that isn't a stale
// tab any more: the fresh build is failing too (a broken deploy, a persistently failing asset), so
// it gets reported.
const FAILURE_PENDING_KEY = 'zs:chunkFailurePending';
const RECOVERED_AT_KEY = 'zs:chunkRecoveredAt';
const REPEAT_WINDOW_MS = 10 * 60_000;

try {
    if (sessionStorage.getItem(FAILURE_PENDING_KEY)) {
        sessionStorage.removeItem(FAILURE_PENDING_KEY);
        sessionStorage.setItem(RECOVERED_AT_KEY, String(Date.now()));
    }
} catch {
    // storage unavailable — failures just never count as repeats
}

function isChunkLoadMessage(message: unknown) {
    return typeof message === 'string' && CHUNK_LOAD_ERROR_PATTERNS.some(re => re.test(message));
}

/** Whether `err` (or anything in its `cause` chain) is a failed dynamic import / chunk preload. */
export function isChunkLoadError(err: unknown): boolean {
    for (let e = err, depth = 0; e && depth < 5; e = (e as { cause?: unknown }).cause, depth++) {
        if (isChunkLoadMessage(typeof e === 'object' ? (e as { message?: unknown }).message : e)) return true;
    }
    return false;
}

/** Note a chunk failure in this tab, so one after the next page load counts as a repeat. */
export function recordChunkLoadFailure() {
    try {
        sessionStorage.setItem(FAILURE_PENDING_KEY, '1');
    } catch {
        // see above
    }
}

/** Whether this page load followed a chunk failure recently — i.e. reloading didn't fix it. */
export function isRepeatChunkLoadFailure() {
    try {
        const recoveredAt = Number(sessionStorage.getItem(RECOVERED_AT_KEY));
        return !!recoveredAt && Date.now() - recoveredAt < REPEAT_WINDOW_MS;
    } catch {
        return false;
    }
}

/**
 * Sentry `beforeSend` filter for chunk-load failures. A first failure is stale-tab noise after a
 * deploy, which the main app recovers from by reloading (wrapRuntimeImport, installChunkLoadRecovery),
 * so it's dropped. One that recurs after the tab has reloaded is reported, tagged `chunkLoadRepeat`.
 * Matches any linked exception, so a wrapper error whose `cause` is the failed import counts too.
 */
export function excludeChunkLoadErrors(event: ErrorEvent): ErrorEvent | null {
    if (!event.exception?.values?.some(exception => isChunkLoadMessage(exception.value))) return event;
    if (isRepeatChunkLoadFailure()) {
        event.tags = { ...event.tags, chunkLoadRepeat: true };
        return event;
    }
    recordChunkLoadFailure();
    return null;
}
